import { ApolloClient, useApolloClient, useMutation } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import {
    BestPracticeAuditHistoryQuery,
    GetBestPracticesForContentQuery,
} from 'api/graphQL/queries/bestPractices.queries'
import { FirmProjectParametersQuery } from 'api/graphQL/queries/firm.queries'
import {
    bpStagedConfig,
    requestBPDetailsRefetch,
    requestBPManagementPanelOpen,
    requestBPRemoveConfirmation,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import { useContentItemElements } from 'components/WorkspaceEditor/components/TreeView/hooks/useContentItemElements'
import { updateBestPracticesForContentItem } from 'components/WorkspaceEditor/features/bestPractices'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import {
    useSelectedProjectElementNode,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useContext } from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import {
    isOMBestPracticeFeatureEnabled,
    isSmartProjectCopyEnabled,
} from 'shared/utils/bestPracticeUtils'
import { BP_MODE, NON_MODIFIER_PARAMETER_KEYS } from '../constants'
import {
    BPStagedConfig,
    ParameterKey,
    parameterConfig,
} from '../types/bpManagement.types'
import { extractFamilyNumberFromTreePath } from '../utils/bestPracticesUtils'
import {
    buildFirmBPParameters,
    FirmDataShape,
    FirmModifierLookup,
    labelToParameterKey,
} from '../utils/firmBPParameterUtils'
import type { BestPracticeDataForContent } from './useBestPracticeDetails'
import type { BPMenuOpenParams } from './useBestPracticesMenu'

type ContentItemNode = Partial<ContentItemViewNodesType>
type SelectedProjectElementNode = ReturnType<
    typeof useSelectedProjectElementNode
>

const hasAnyParameterSelection = (config: BPStagedConfig): boolean => {
    if (config.projectGroup) return true
    return parameterConfig.some(
        ({ key }) =>
            key !== NON_MODIFIER_PARAMETER_KEYS.PROJECT_GROUP &&
            config.parameterSelected[key].length > 0
    )
}

const NON_MODIFIER_KEY_SET: readonly ParameterKey[] = Object.values(
    NON_MODIFIER_PARAMETER_KEYS
)

const buildProjectParametersInput = (
    config: BPStagedConfig,
    lookup: FirmModifierLookup
): { modifierListId: string; selectedModifierIds: string[] }[] => {
    const result: {
        modifierListId: string
        selectedModifierIds: string[]
    }[] = []
    parameterConfig.forEach(({ key }) => {
        // City/Province-State map to the mutation's `location` field, not a
        // firm modifier-list parameter - handled separately in buildLocationInput.
        if (NON_MODIFIER_KEY_SET.includes(key)) {
            return
        }
        const selectedNames = config.parameterSelected[key]
        if (selectedNames.length === 0) return
        const entry = lookup[key]
        if (!entry) return
        const ids = selectedNames
            .map((name) => entry.modifiers[name])
            .filter(Boolean)
        if (ids.length > 0) {
            result.push({
                modifierListId: entry.modifierListId,
                selectedModifierIds: ids,
            })
        }
    })
    return result
}

const buildLocationInput = (
    config: BPStagedConfig
): { city?: string; region?: string } | undefined => {
    if (!config.city && !config.provinceState) return undefined
    return {
        ...(config.city && { city: config.city }),
        ...(config.provinceState && { region: config.provinceState }),
    }
}

/** Locate the matching node in the workspace store (handles hyperlink pills). */
const findContentItemNode = (
    nodes: ContentItemNode[],
    params: BPMenuOpenParams
): ContentItemNode | undefined => {
    if (!params.contentItemId) return undefined
    const isHyperLink = params.contentItemType?.toLowerCase() === 'hyperlink'
    return nodes.find((item) =>
        isHyperLink
            ? item.pillId === params.contentItemId
            : item.id === params.contentItemId
    )
}

/**
 * Returns true when `bp` represents the same configuration as `config`.
 * For Global mode: any global BP is a match.
 * For Parameter mode: exact match on every parameter key and its selected
 * modifier names, plus project group. Uses `labelToParameterKey` to map the
 * server's modifier-list display name back to a ParameterKey.
 */
const isBPMatchingStagedConfig = (
    bp: BestPracticeDataForContent,
    config: BPStagedConfig
): boolean => {
    if (config.mode === BP_MODE.GLOBAL) return Boolean(bp.isGlobal)

    if (config.mode === BP_MODE.PARAMETER) {
        if (bp.isGlobal) return false

        // Build server-side selection: ParameterKey → Set of modifier names
        const serverParams: Partial<Record<string, Set<string>>> = {}
        for (const p of bp.parameters ?? []) {
            const label = p.modifierList?.name?.trim()
            if (!label) continue
            const key = labelToParameterKey[label]
            if (!key) continue
            serverParams[key] = new Set(
                (p.selectedModifiers ?? [])
                    .map((m) => m.name)
                    .filter((n): n is string => Boolean(n))
            )
        }

        // Build staged selection: only keys with non-empty selections
        const stagedParams: Partial<Record<string, Set<string>>> = {}
        for (const { key } of parameterConfig) {
            if (key === 'projectGroup') continue
            const vals = config.parameterSelected[key]
            if (vals.length > 0) stagedParams[key] = new Set(vals)
        }

        // Exact match: same keys, same values per key
        const serverKeys = Object.keys(serverParams)
        const stagedKeys = Object.keys(stagedParams)
        if (serverKeys.length !== stagedKeys.length) return false
        for (const key of stagedKeys) {
            const serverSet = serverParams[key]
            const stagedSet = stagedParams[key]!
            if (!serverSet || serverSet.size !== stagedSet.size) return false
            for (const v of stagedSet) if (!serverSet.has(v)) return false
        }

        return (bp.projectGroup ?? undefined) === (config.projectGroup ?? undefined)
    }

    return false
}

/**
 * Fetches all BPs on the content item and returns the id of the one matching
 * the staged config (if any) alongside the total count. Skips the network call
 * when the store already shows no BP — nothing can match in that case.
 */
const findMatchingBP = async (
    apolloClient: ApolloClient<unknown>,
    params: BPMenuOpenParams,
    config: BPStagedConfig,
    storeShowsExistingBP: boolean
): Promise<{ matchingId: string | undefined; totalCount: number }> => {
    if (!storeShowsExistingBP) return { matchingId: undefined, totalCount: 0 }
    try {
        const { data } = await apolloClient.query<{
            getBestPracticesForContent: BestPracticeDataForContent[] | null
        }>({
            query: GetBestPracticesForContentQuery,
            variables: {
                parentId: params.contentItemId,
                projectId: params.projectId,
            },
            fetchPolicy: 'no-cache',
        })
        const bps = data?.getBestPracticesForContent ?? []
        const match = bps.find((bp) => isBPMatchingStagedConfig(bp, config))
        return { matchingId: match?.id, totalCount: bps.length }
    } catch {
        return { matchingId: undefined, totalCount: 0 }
    }
}

interface BuildMutationInputArgs {
    params: BPMenuOpenParams
    contentItemNode: ContentItemNode | undefined
    selectedProjectElementNode: SelectedProjectElementNode
    getElementsForNode: (id: string) => unknown
    getRecursiveElementsForNode: (id: string) => unknown
    isCurrentlyOnOMWorkspace: boolean
}

/**
 * Build the shared portion of the AddUpdateBestPractice mutation input —
 * mirrors BestPracticesSettingPopover so the BE sees the same payload as the
 * right-click "Apply" path.
 */
const buildBPMutationInput = ({
    params,
    contentItemNode,
    selectedProjectElementNode,
    getElementsForNode,
    getRecursiveElementsForNode,
    isCurrentlyOnOMWorkspace,
}: BuildMutationInputArgs): Record<string, unknown> => {
    const isManufacturerPill =
        params.elementId !== undefined &&
        (contentItemNode?.isManufacturers || contentItemNode?.isBOD)

    const input: Record<string, unknown> = {
        parentProjectId: params.projectId,
    }
    // Send the OM section-version id on every Set/Modify so the BE audit log
    // can be queried by iterationId. Gated behind the OM BP feature flag so
    // the field is never sent while the audit feature is off.
    if (
        isOMBestPracticeFeatureEnabled() &&
        isCurrentlyOnOMWorkspace &&
        selectedProjectElementNode?.iteration?.id
    ) {
        input.iterationId = selectedProjectElementNode.iteration.id
    }
    if (isManufacturerPill) {
        input.listingId = params.elementId
        input.familyNumber = extractFamilyNumberFromTreePath(
            selectedProjectElementNode?.parentTreePath
        )
        input.productType = selectedProjectElementNode?.text
        input.projectElementNodeId = selectedProjectElementNode?.id
    } else {
        input.contentItemId = params.contentItemId
        input.contentItemElementId = params.elementId
        input.projectElementNodeId = selectedProjectElementNode?.id
    }
    if (isSmartProjectCopyEnabled()) {
        if (!params.elementId) {
            const isUserAdded = contentItemNode?.isUserAdded ?? false
            input.elements = isUserAdded
                ? getRecursiveElementsForNode(params.contentItemId)
                : getElementsForNode(params.contentItemId)
            input.isUserAddedContentBestPractice = isUserAdded
        }
        input.isHidden = contentItemNode?.isHidden ?? false
    }
    return input
}

/**
 * Apply the optimistic BP update to the workspace store and return a revert
 * callback. The caller invokes the revert if the mutation fails.
 *
 * When removing one of several BPs (`willRemove && remainingCount > 0`), the
 * store flags are left unchanged — the content item still has other BPs so the
 * icon should stay visible. The count badge will self-correct once the
 * post-mutation refetch tick lands.
 */
const applyOptimisticBPUpdate = (
    params: BPMenuOpenParams,
    config: BPStagedConfig,
    willRemove: boolean,
    remainingCount: number
): (() => void) => {
    const storeState = useWorkspace2Store.getState()
    const previousNodes = storeState.contentItemViewNodes
    const revert = () =>
        useWorkspace2Store
            .getState()
            .actions.setContentItemViewNodes(previousNodes)

    if (willRemove && remainingCount > 0) {
        // Other BPs still exist after this removal — don't flip the flags.
        // Icon stays shown; tooltip refetch corrects the count badge.
        return revert
    }

    const willHaveParameterBP = !willRemove && config.mode === BP_MODE.PARAMETER
    const willHaveGlobalBP = !willRemove && config.mode === BP_MODE.GLOBAL
    const updatedNodes = updateBestPracticesForContentItem(
        params.contentItemId,
        params.elementId,
        previousNodes,
        willHaveParameterBP,
        willHaveGlobalBP
    )
    storeState.actions.setContentItemViewNodes(updatedNodes)
    return revert
}

/**
 * Returns a function that applies (or removes) the BP Management panel's
 * staged config to a content item, intended for double-click handlers on
 * tree nodes and inline pills.
 */
export const useApplyBPFromConfig = () => {
    const apolloClient = useApolloClient()
    const { currentUserActiveFirm } = useContext(AppBarContext)
    const firmId = currentUserActiveFirm?.firmId
    const selectedProjectElementNode = useSelectedProjectElementNode()
    const {
        getRecursiveElementsForNode,
        getElementsForNode,
    } = useContentItemElements()
    const notify = useNotification()
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

    const [addUpdateBestPractice] = useMutation(AddUpdateBestPracticeMutation, {
        onCompleted: () => notify.success({ message: 'Best practice applied' }),
        onError: () =>
            notify.error({
                message: 'Failed to apply best practice. Please try again.',
            }),
    })

    const [removeBestPractice] = useMutation(RemoveBestPracticeMutation, {
        onCompleted: () => notify.success({ message: 'Best practice removed' }),
        onError: () =>
            notify.error({
                message: 'Failed to remove best practice. Please try again.',
            }),
    })

    return useCallback(
        async (params: BPMenuOpenParams) => {
            requestBPManagementPanelOpen()
            const config = bpStagedConfig()

            const canAdd =
                config.mode === BP_MODE.GLOBAL ||
                (config.mode === BP_MODE.PARAMETER &&
                    hasAnyParameterSelection(config))

            const initialState = useWorkspace2Store.getState()
            const contentItemNode = findContentItemNode(
                initialState.contentItemViewNodes,
                params
            )
            const storeShowsExistingBP = Boolean(
                contentItemNode?.isBestPractice ||
                    contentItemNode?.isGlobalBestPractice
            )

            // Fetch all existing BPs and find the one matching the current
            // staged config. If none matches, we treat this as a new addition
            // (even when other BPs already exist on this content item).
            const { matchingId, totalCount } = await findMatchingBP(
                apolloClient,
                params,
                config,
                storeShowsExistingBP
            )

            if (!matchingId && !canAdd) {
                notify.info({
                    message:
                        'Select a configuration in the Best Practice Management panel to apply a Best Practice',
                })
                return
            }

            if (matchingId) {
                const confirmed = await requestBPRemoveConfirmation()
                if (!confirmed) return
            }

            notify.loading({
                message: matchingId
                    ? 'Removing best practice...'
                    : 'Applying best practice...',
            })

            const willRemove = Boolean(matchingId)
            // BPs remaining after this operation — used by the optimistic
            // update to decide whether to clear the store's BP flags.
            const remainingCount = willRemove ? totalCount - 1 : totalCount
            const revertOptimistic = applyOptimisticBPUpdate(
                params,
                config,
                willRemove,
                remainingCount
            )

            // Refetch the OM audit history after a successful Set/Modify/Remove
            // so the tips list reflects the new entry without a page refresh.
            // Empty list when the OM BP feature is off → no wasted round-trip.
            const iterationId = selectedProjectElementNode?.iteration?.id
            const auditRefetch =
                isOMBestPracticeFeatureEnabled() &&
                isCurrentlyOnOMWorkspace &&
                iterationId
                    ? [
                          {
                              query: BestPracticeAuditHistoryQuery,
                              variables: { iterationId },
                          },
                      ]
                    : []

            // Bump the per-content-item tick after the mutation lands so the
            // BestPracticesIconWrapper's tooltip query fires AFTER the BE has
            // created/updated/removed the BP (not in parallel with the
            // optimistic icon flip, which returns stale null data).
            const triggerDetailsRefetch = () => {
                if (params.contentItemId) {
                    requestBPDetailsRefetch(params.contentItemId)
                }
            }

            if (matchingId) {
                removeBestPractice({
                    variables: { input: { id: matchingId } },
                    refetchQueries: auditRefetch,
                })
                    .then(triggerDetailsRefetch)
                    .catch(revertOptimistic)
                return
            }

            // Past this point matchingId is falsy and canAdd must be truthy
            // (the early-return above eliminated the !matchingId && !canAdd case).

            const commonInput = buildBPMutationInput({
                params,
                contentItemNode,
                selectedProjectElementNode,
                getElementsForNode,
                getRecursiveElementsForNode,
                isCurrentlyOnOMWorkspace,
            })

            if (config.mode === BP_MODE.GLOBAL) {
                addUpdateBestPractice({
                    variables: {
                        input: { ...commonInput, isGlobal: true },
                    },
                    refetchQueries: auditRefetch,
                })
                    .then(triggerDetailsRefetch)
                    .catch(revertOptimistic)
                return
            }

            const firmCacheData = firmId
                ? apolloClient.readQuery<FirmDataShape>({
                      query: FirmProjectParametersQuery,
                      variables: { firmId },
                  })
                : null
            const { lookup } = buildFirmBPParameters(firmCacheData)
            const projectParameters = buildProjectParametersInput(
                config,
                lookup
            )
            const location = buildLocationInput(config)

            // BE requires at least one parameter group, a project group, or a location.
            if (
                projectParameters.length === 0 &&
                !config.projectGroup &&
                !location
            ) {
                revertOptimistic()
                return
            }

            addUpdateBestPractice({
                variables: {
                    input: {
                        ...commonInput,
                        isGlobal: false,
                        projectParameters,
                        projectGroup: config.projectGroup,
                        location,
                    },
                },
                refetchQueries: auditRefetch,
            })
                .then(triggerDetailsRefetch)
                .catch(revertOptimistic)
        },
        [
            apolloClient,
            addUpdateBestPractice,
            removeBestPractice,
            firmId,
            selectedProjectElementNode,
            getRecursiveElementsForNode,
            getElementsForNode,
            notify,
            isCurrentlyOnOMWorkspace,
        ]
    )
}
